/**
 * Perks — cross-Clan Perks discovery feed at /perks.
 *
 * Mirror of /bounties. Every active perk across all Clans, sortable, with
 * search + "Load more" pagination. Card click deep-links to the parent
 * Clan Feed → Perks tab where redemption gating is enforced.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '../components/AppShell';
import api from '../api';

const SORT_KEYS = ['newest', 'cheapest', 'lowest_gate'];

export default function Perks() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sort, setSort] = useState('newest');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (s) => {
    setItems(null);
    try {
      const { data } = await api.get('/perks/feed', { params: { sort: s, limit: 50 } });
      setItems(data?.perks || []);
      setHasMore(!!data?.has_more);
    } catch {
      setItems([]);
      setHasMore(false);
    }
  }, []);

  useEffect(() => { load(sort); }, [sort, load]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get('/perks/feed', {
        params: { sort, limit: 50, skip: (items || []).length },
      });
      setItems((prev) => [...(prev || []), ...(data?.perks || [])]);
      setHasMore(!!data?.has_more);
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = useMemo(() => {
    if (!items) return items;
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((p) => (
      (p.title || '').toLowerCase().includes(term) ||
      (p.description || '').toLowerCase().includes(term) ||
      (p.creator_handle || '').toLowerCase().includes(term) ||
      (p.creator_display_name || '').toLowerCase().includes(term)
    ));
  }, [items, search]);

  const sortLabel = (k) => (
    k === 'newest' ? t('perks.sortNewest')
      : k === 'cheapest' ? t('perks.sortCheapest')
      : t('perks.sortEasy')
  );

  return (
    <AppShell activeTab="perks">
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }} data-testid="perks-page">
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 4, fontFamily: "'Baloo 2', sans-serif" }}>
            {t('perks.title')}
          </h1>
          <p style={{ fontSize: 14, opacity: 0.7, margin: 0 }}>
            {t('perks.subtitle')}
          </p>
        </div>

        <div className="discovery-toolbar">
          <div className="discovery-search">
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
              <path d="M14 14l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder={t('perks.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="perks-search"
            />
          </div>
          <div className="discovery-filters">
            {SORT_KEYS.map((k) => (
              <button
                key={k}
                onClick={() => setSort(k)}
                className={`discovery-chip${sort === k ? ' active' : ''}`}
                data-testid={`perks-sort-${k}`}
              >
                {sortLabel(k)}
              </button>
            ))}
          </div>
        </div>

        {items === null && (
          <div style={{ padding: '60px 0', textAlign: 'center', opacity: 0.7 }} data-testid="perks-loading">
            {t('perks.loading')}
          </div>
        )}

        {items && filtered.length === 0 && (
          <div style={{ padding: '60px 0', textAlign: 'center', opacity: 0.6 }} data-testid="perks-empty">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
              {search.trim()
                ? t('perks.noMatchesTitle', { term: search.trim() })
                : t('perks.emptyTitle')}
            </div>
            <div style={{ fontSize: 13 }}>
              {search.trim() ? t('perks.noMatchesBody') : t('perks.emptyBody')}
            </div>
          </div>
        )}

        {items && filtered.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {filtered.map((p) => (
              <PerkFeedCard
                key={p.id}
                perk={p}
                onOpen={() => navigate(`/clan/${p.clan_handle}/feed?tab=perks`, { state: { from: '/perks' } })}
              />
            ))}
          </div>
        )}

        {hasMore && !search.trim() && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button
              className="mc-btn-secondary"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="perks-load-more"
            >
              {loadingMore ? t('perks.loadMoreBusy') : t('perks.loadMore')}
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}


function PerkFeedCard({ perk, onOpen }) {
  const { t } = useTranslation();
  const eligible = (perk.viewer_bits_owned || 0) >= (perk.required_bits || 0);
  const gradient = perk.creator_gradient
    ? `linear-gradient(135deg, ${perk.creator_gradient[0]}, ${perk.creator_gradient[1]})`
    : 'linear-gradient(135deg, #FF7043, #F2679E)';
  const initials = (perk.creator_display_name || perk.creator_handle || 'C')
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <button
      className="home-perk-card"
      onClick={onOpen}
      data-testid={`perks-card-${perk.id}`}
    >
      <div className="home-perk-card-head">
        {perk.creator_avatar ? (
          <img src={perk.creator_avatar} alt="" className="home-perk-avatar" />
        ) : (
          <div className="home-perk-avatar" style={{ background: gradient }}>{initials}</div>
        )}
        <div className="home-perk-creator">
          <div className="home-perk-creator-name">{perk.creator_display_name || perk.creator_handle}</div>
          <div className="home-perk-creator-handle">@{perk.creator_handle}</div>
        </div>
      </div>

      <div className="home-perk-title">{perk.title}</div>
      {perk.description && (
        <div className="home-perk-desc">{perk.description}</div>
      )}

      <div className="home-perk-chips">
        <span className="chip chip-price">{`${Number(perk.points_cost || 0).toLocaleString()} ${t('common.pts')}`}</span>
        {perk.required_bits > 0 && (
          <span className={`chip ${eligible ? 'chip-gate-ok' : 'chip-gate-lock'}`}>
            {t('perks.requiredBits', { count: perk.required_bits })}
          </span>
        )}
        {perk.already_redeemed && (
          <span className="chip chip-redeemed">{t('perks.redeemed')}</span>
        )}
      </div>
    </button>
  );
}
